import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { ArrowLeft, Save, Trash2, Upload } from "lucide-react";
import { toast, Toaster } from "sonner";

const PROVINCES = [
  { value: "QC", label: "Québec (QC)" },
  { value: "ON", label: "Ontario (ON)" },
  { value: "AB", label: "Alberta (AB)" },
  { value: "BC", label: "Colombie-Britannique (BC)" },
  { value: "MB", label: "Manitoba (MB)" },
  { value: "SK", label: "Saskatchewan (SK)" },
  { value: "NB", label: "Nouveau-Brunswick (NB)" },
  { value: "NS", label: "Nouvelle-Écosse (NS)" },
  { value: "NL", label: "Terre-Neuve-et-Labrador (NL)" },
  { value: "PE", label: "Île-du-Prince-Édouard (PE)" },
  { value: "NT", label: "Territoires du Nord-Ouest (NT)" },
  { value: "NU", label: "Nunavut (NU)" },
  { value: "YT", label: "Yukon (YT)" },
  { value: "Ext.Canada", label: "Hors Canada" },
  { value: "Avion/Plane", label: "Avion / Vol" },
];

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [categories, setCategories] = useState([]);
  const [reports, setReports] = useState([]);
  const [inv, setInv] = useState(
    isNew
      ? { date: new Date().toISOString().slice(0, 10), merchant: "", description: "", participants: "", province: "QC", category: "representation_repas", total: 0, subtotal: 0, tps: 0, tvq: 0, tvh: 0, tip: 0, report_id: null }
      : null
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [imgRefresh, setImgRefresh] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data: cats } = await api.get("/categories");
      setCategories(cats);
      try {
        // Show all non-archived reports so invoices can be moved freely
        const { data: reps } = await api.get("/reports");
        setReports((reps || []).filter((r) => r.status !== "archived"));
      } catch { /* ignore */ }
      if (!isNew) {
        try {
          const { data } = await api.get(`/invoices/${id}`);
          setInv(data);
        } catch {
          toast.error("Facture introuvable");
          navigate("/dashboard");
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [id, isNew, navigate]);

  const set = (k, v) => setInv((p) => ({ ...p, [k]: v }));

  // Coerce any value to a safe string (handles arrays, numbers, null, etc. from OCR)
  const asStr = (v) => {
    if (v == null) return "";
    if (Array.isArray(v)) return v.filter(Boolean).map(String).join(", ");
    return String(v);
  };

  // Sanitize a number input: return 0 instead of NaN/empty/negative-only.
  // Accepts French comma as decimal separator (e.g. "12,50" → 12.5)
  const num = (v) => {
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    const cleaned = String(v ?? "").replace(",", ".").trim();
    if (cleaned === "" || cleaned === "-" || cleaned === ".") return 0;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  // Format a number for display in the input. Empty input shows empty (not "0"),
  // so the user can clear the field and type fresh.
  const showNum = (v) => {
    if (v === null || v === undefined || v === "") return "";
    return String(v);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build a clean payload with only the fields the backend accepts
      const payload = {
        date: inv.date || null,
        merchant: asStr(inv.merchant).trim() || null,
        description: asStr(inv.description).trim() || null,
        participants: asStr(inv.participants).trim() || null,
        province: inv.province || "QC",
        category: inv.category || null,
        total: num(inv.total),
        subtotal: num(inv.subtotal),
        tps: num(inv.tps),
        tvq: num(inv.tvq),
        tvh: num(inv.tvh),
        tip: num(inv.tip),
        report_id: inv.report_id || null,
      };
      if (isNew) {
        const { data } = await api.post("/invoices/manual", payload);
        toast.success("Facture créée");
        navigate(`/invoice/${data.invoice_id}`, { replace: true });
      } else {
        const { data } = await api.put(`/invoices/${id}`, payload);
        // Preserve enriched fields (image_filename, ocr_raw, etc.) from current state
        setInv((prev) => ({ ...prev, ...data }));
        toast.success("Modifications enregistrées");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (e?.message || "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Supprimée");
      navigate("/dashboard");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const attachImage = async (file) => {
    if (!file || isNew) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/invoices/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImgRefresh((n) => n + 1);
      toast.success("Image ajoutée");
    } catch {
      toast.error("Upload impossible");
    }
  };

  if (loading || !inv) return (
    <Layout><div className="progress-line max-w-xl" /></Layout>
  );

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <button className="btn-ghost mb-4" onClick={() => navigate("/dashboard")} data-testid="back-button">
        <ArrowLeft size={14} /> Retour
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Image */}
        <div className="lg:col-span-2">
          <div className="ra-card p-3 sticky top-24">
            {!isNew && inv.image_filename ? (
              <img
                key={imgRefresh}
                src={`${API}/invoices/${id}/image?r=${imgRefresh}`}
                alt="facture"
                className="w-full h-auto max-h-[70vh] object-contain bg-gray-50"
                data-testid="invoice-image"
              />
            ) : (
              <div className="h-80 flex items-center justify-center bg-gray-50 text-gray-400 text-sm">
                Aucune image
              </div>
            )}
            {!isNew && (
              <div className="mt-3">
                <button className="btn-secondary w-full justify-center" onClick={() => fileInputRef.current?.click()} data-testid="replace-image-button">
                  <Upload size={14} /> {inv.image_filename ? "Remplacer l'image" : "Ajouter une image"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => attachImage(e.target.files?.[0])}
                />
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <p className="overline mb-2">{isNew ? "Nouvelle saisie" : "Édition"}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none mb-6">
            {isNew ? "Saisir une facture" : (inv.merchant || "Facture")}
          </h1>
          {inv.is_restaurant && (
            <div className="mb-4 badge restaurant">Détectée comme restaurant — règle auto appliquée</div>
          )}
          {inv.ocr_notes && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-100" data-testid="ocr-notes">
              <span className="font-semibold">Note IA :</span> {inv.ocr_notes}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date"><input type="date" className="ra-input" value={inv.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="field-date" /></Field>
            <Field label="Province / Lieu">
              <select className="ra-input" value={inv.province || "QC"} onChange={(e) => set("province", e.target.value)} data-testid="field-province">
                {PROVINCES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Fournisseur (Merchant)" full><input type="text" className="ra-input" value={inv.merchant || ""} onChange={(e) => set("merchant", e.target.value)} data-testid="field-merchant" /></Field>
            <Field label="Description" full><input type="text" className="ra-input" value={inv.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="field-description" /></Field>
            <Field label="Participant(s)" full>
              <input type="text" className="ra-input" value={asStr(inv.participants)} onChange={(e) => set("participants", e.target.value)} placeholder="ex: Jean, Marie" data-testid="field-participants" />
            </Field>
            <Field label="Catégorie" full>
              <select className="ra-input" value={inv.category || ""} onChange={(e) => set("category", e.target.value)} data-testid="field-category">
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>

            <Field label="Rapport" full>
              <select className="ra-input" value={inv.report_id || ""} onChange={(e) => set("report_id", e.target.value || null)} data-testid="field-report">
                <option value="">— Aucun —</option>
                {reports.map((r) => <option key={r.report_id} value={r.report_id}>{r.name}</option>)}
              </select>
            </Field>

            <Field label="Total ($)"><input type="text" inputMode="decimal" className="ra-input num" value={showNum(inv.total)} onChange={(e) => set("total", e.target.value)} data-testid="field-total" /></Field>
            <Field label="Sous-total ($)"><input type="text" inputMode="decimal" className="ra-input num" value={showNum(inv.subtotal)} onChange={(e) => set("subtotal", e.target.value)} data-testid="field-subtotal" /></Field>
            <Field label="TPS/GST ($)"><input type="text" inputMode="decimal" className="ra-input num" value={showNum(inv.tps)} onChange={(e) => set("tps", e.target.value)} data-testid="field-tps" /></Field>
            <Field label="TVQ/QST ($)"><input type="text" inputMode="decimal" className="ra-input num" value={showNum(inv.tvq)} onChange={(e) => set("tvq", e.target.value)} data-testid="field-tvq" /></Field>
            <Field label="TVH/HST ($)"><input type="text" inputMode="decimal" className="ra-input num" value={showNum(inv.tvh)} onChange={(e) => set("tvh", e.target.value)} data-testid="field-tvh" /></Field>
            <Field label="Pourboire ($)"><input type="text" inputMode="decimal" className="ra-input num" value={showNum(inv.tip)} onChange={(e) => set("tip", e.target.value)} data-testid="field-tip" /></Field>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-button">
              <Save size={14} /> {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
            {!isNew && (
              <button className="btn-danger" onClick={remove} data-testid="delete-button">
                <Trash2 size={14} /> Supprimer
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="ra-label">{label}</label>
      {children}
    </div>
  );
}
